<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
// Overrides @basecom-gmbh/pond/components/Layout/LayoutSlider.vue
import type Swiper from 'swiper';

const props = withDefaults(
    defineProps<{
        autoSlide?: boolean;
        autoplayTimeout?: number;
        speed?: number;
        navigationDots?: boolean;
        navigationArrows?: boolean;
        displayMode?: string;
        minHeight?: string;
        classes?: string;
        loop?: boolean;
        direction?: string;
        spaceBetween?: number;
        slidesPerView?: number;
        thumbsSwiper?: string;
        breakpoints?: object;
        init?: boolean;
        verticalNavigation?: boolean;
        thumbRef?: string;
        leftArrowClass?: { [key: string]: boolean };
        rightArrowClass?: { [key: string]: boolean };
    }>(),
    {
        autoSlide: false,
        autoplayTimeout: 3000,
        speed: 300,
        navigationDots: false,
        navigationArrows: true,
        displayMode: 'cover',
        minHeight: '300',
        classes: null,
        loop: true,
        direction: 'horizontal',
        spaceBetween: 0,
        slidesPerView: 1,
        thumbsSwiper: null,
        // eslint-disable-next-line vue/require-valid-default-prop
        breakpoints: {},
        init: false,
        verticalNavigation: false,
        thumbRef: null,
        leftArrowClass: undefined,
        rightArrowClass: undefined,
    },
);

const sliderRef: Swiper = ref();
const prevSlide = ref(null);
const nextSlide = ref(null);
const navigation = props.navigationArrows ? ref(null) : false;

watch([prevSlide, nextSlide, sliderRef], ([prevSlideValue, nextSlideValue]) => {
    if (prevSlideValue && nextSlideValue && props.navigationArrows) {
        const swiperParams = {
            navigation: {
                prevEl: prevSlideValue,
                nextEl: nextSlideValue,
            },
        };

        Object.assign(sliderRef.value, swiperParams);
    }

    sliderRef.value.initialize();
});
</script>

<template>
    <!-- IDS-84 - change slider styles -->
    <ClientOnly>
        
<FormKitLazyProvider config-file="true">
<div
            class="relative w-full"
            :class="classes"
        >
            <template v-if="navigationArrows">
                <!-- IDS-221 - add styling for slides -->
                <div
                    ref="prevSlide"
                    class="absolute z-10"
                    :class="[
                        leftArrowClass,
                        {
                            'left-1/2 top-0 flex w-full -translate-x-1/2 justify-center py-1 lg:py-2':
                                verticalNavigation,
                            'left-5 top-1/2 -translate-y-1/2 px-1 py-6 sm:left-0 lg:px-2 lg:py-12': !verticalNavigation,
                        },
                    ]"
                >
                    <!-- IDS-221 - change icon -->
                    <FormKitIcon
                        :icon="verticalNavigation ? 'chevron-up' : 'ids_arrow-left'"
                        class="block h-4 w-4 text-neutral-800"
                    />
                </div>

                <div
                    ref="nextSlide"
                    class="absolute z-10"
                    :class="[
                        rightArrowClass,
                        {
                            'bottom-0 left-1/2 flex w-full -translate-x-1/2 justify-center py-1 lg:py-2':
                                verticalNavigation,
                            'right-5 top-1/2 -translate-y-1/2 px-1 py-6 sm:right-0 lg:px-2 lg:py-12':
                                !verticalNavigation,
                        },
                    ]"
                >
                    <!-- IDS-221 - change icon -->
                    <FormKitIcon
                        :icon="verticalNavigation ? 'chevron-down' : 'ids_arrow-right'"
                        class="block h-4 w-4 text-neutral-800"
                    />
                </div>
            </template>
            <!-- IDS-84 - change slider pagination based on breakpoint -->
            <swiper-container
                ref="sliderRef"
                class="grid h-full w-full"
                :class="thumbRef ? thumbRef : `min-h-[${minHeight}px]`"
                :autoplay="autoSlide"
                :speed="speed"
                :pagination="navigationDots"
                :navigation="navigation"
                :loop="loop"
                :direction="direction"
                :space-between="spaceBetween"
                :slides-per-view="slidesPerView"
                :thumbs-swiper="thumbsSwiper"
                :breakpoints="breakpoints"
                :init="init"
            >
                <slot />
            </swiper-container>
        </div>
</FormKitLazyProvider>

    </ClientOnly>
</template>
